import { snippetModalAtom } from '@/components/modal/snippet-editor-modal-atoms';
import { useQueryUserSnippets } from '@/hooks/query/useQueryUserSnippets';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Card, CardContent } from '@heidi/design-system/card';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { useSetAtom } from 'jotai';
import { Pencil } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { type Command, type CommandListProps } from './types';

const keepEditorFocus = (event: React.MouseEvent) => event.preventDefault();

export const CommandList = forwardRef((props: CommandListProps, ref) => {
  const scrollContainer = useRef<HTMLDivElement>(null);
  const activeItem = useRef<HTMLButtonElement>(null);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const setIsOpen = useSetAtom(snippetModalAtom);

  const { data: userSnippets } = useQueryUserSnippets();

  // Anytime the groups change, i.e. the user types to narrow it down, we want to
  // reset the current selection to the first menu item
  useEffect(() => {
    setSelectedGroupIndex(0);
    setSelectedCommandIndex(0);
  }, [props]);

  const selectItem = useCallback(
    (groupIndex: number, commandIndex: number) => {
      const command = props.items.at(groupIndex)?.commands.at(commandIndex);
      if (command) {
        // Add special handling for new snippet command
        if (command.name === 'createSnippet') {
          props.editor.chain().focus().deleteRange(props.range).run();
          setIsOpen({
            open: true,
            from: 'editor',
            isEditing: false,
            initialSnippet: {
              shorthand: '',
              content: '',
            },
          });
        } else {
          props.command(command);
        }
      }
    },
    [props, setIsOpen]
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === 'ArrowDown') {
        if (!props.items.length) {
          return false;
        }

        const commands = props.items.at(selectedGroupIndex)?.commands;

        let newCommandIndex = selectedCommandIndex + 1;
        let newGroupIndex = selectedGroupIndex;

        if (commands && commands.length - 1 < newCommandIndex) {
          newCommandIndex = 0;
          newGroupIndex = selectedGroupIndex + 1;
        }

        if (props.items.length - 1 < newGroupIndex) {
          newGroupIndex = 0;

          scrollContainer.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else if (activeItem.current && scrollContainer.current) {
          const containerRect = scrollContainer.current.getBoundingClientRect();
          const itemRect = activeItem.current.getBoundingClientRect();

          // If the item is not visible or partially visible, scroll to it
          if (itemRect.bottom + itemRect.height - containerRect.bottom > 0) {
            scrollContainer.current.scrollTo({
              top:
                scrollContainer.current.scrollTop +
                (itemRect.bottom + itemRect.height - containerRect.bottom) +
                itemRect.height / 2,
              behavior: 'smooth',
            });
          }
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      if (event.key === 'ArrowUp') {
        if (!props.items.length) {
          return false;
        }

        let newCommandIndex = selectedCommandIndex - 1;
        let newGroupIndex = selectedGroupIndex;

        if (newCommandIndex < 0) {
          newGroupIndex = selectedGroupIndex - 1;
          newCommandIndex = (props.items.at(newGroupIndex)?.commands?.length ?? 1) - 1;
        }

        if (newGroupIndex < 0) {
          newGroupIndex = props.items.length - 1;
          newCommandIndex = (props.items.at(newGroupIndex)?.commands?.length ?? 1) - 1;

          scrollContainer.current?.scrollTo({
            top: scrollContainer.current.scrollHeight,
            behavior: 'smooth',
          });
        } else if (newGroupIndex === 0 && newCommandIndex === 0) {
          scrollContainer.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else if (activeItem.current && scrollContainer.current) {
          // If the item is the first item in the first group, scroll to the top
          const containerRect = scrollContainer.current.getBoundingClientRect();
          const itemRect = activeItem.current.getBoundingClientRect();
          // If the item is not visible or partially visible, scroll to it
          if (containerRect.top - (itemRect.top - itemRect.height) > 0) {
            scrollContainer.current.scrollTo({
              top:
                scrollContainer.current.scrollTop -
                (containerRect.top - (itemRect.top - itemRect.height)) -
                itemRect.height / 2,
              behavior: 'smooth',
            });
          }
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      // if user presses meta | ctrl + enter and selected item is a snippet, open the snippet modal
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        const selectedGroup = props.items.at(selectedGroupIndex);
        const selectedCommand = selectedGroup?.commands.at(selectedCommandIndex);

        if (selectedGroup?.name === 'snippets' && selectedCommand) {
          props.editor.chain().focus().deleteRange(props.range).run();
          setIsOpen({
            open: true,
            from: 'editor',
            isEditing: true,
            initialSnippet: {
              shorthand: selectedCommand.name,
              content: selectedCommand.description ?? '',
              is_verbal_trigger: selectedCommand.snippetIsVerbalTrigger ?? false,
              trigger_phrase: selectedCommand.snippetTriggerPhrase ?? '',
              id: selectedCommand.snippetId,
            },
          });
          return true;
        }
        return false;
      }

      if (event.key === 'Enter') {
        if (
          !props.items.length ||
          selectedGroupIndex === -1 ||
          selectedCommandIndex === -1
        ) {
          return false;
        }

        selectItem(selectedGroupIndex, selectedCommandIndex);

        return true;
      }

      return false;
    },
  }));

  const createCommandClickHandler = useCallback(
    (groupIndex: number, commandIndex: number) => {
      return () => {
        selectItem(groupIndex, commandIndex);
      };
    },
    [selectItem]
  );

  if (!props.items.length) {
    return null;
  }

  return (
    <Card className="w-max min-w-64 max-w-xs" onMouseDown={keepEditorFocus}>
      <CardContent
        ref={scrollContainer}
        className="max-h-[min(80vh,24rem)] overflow-y-auto px-2 py-3"
        onWheel={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          {props.items.map((group, groupIndex: number) => (
            <div key={`${group.title}-wrapper`} className="flex flex-col">
              <TypographyCaptionBold
                className="px-2 pb-2 text-text-secondary"
                key={`${group.title}`}
              >
                {group.title}
              </TypographyCaptionBold>
              {group.name === 'snippets' && userSnippets?.snippets.length === 0 && (
                <TypographyCaption className="px-1 py-2.5 text-text-secondary">
                  No snippets yet
                </TypographyCaption>
              )}
              {group.commands.map((command: Command, commandIndex: number) => {
                const isActive =
                  selectedGroupIndex === groupIndex &&
                  selectedCommandIndex === commandIndex;
                return (
                  <Button
                    disableAutomaticSvgSizing
                    type="button"
                    key={`${groupIndex}-${commandIndex}`}
                    ref={
                      selectedGroupIndex === groupIndex &&
                      selectedCommandIndex === commandIndex
                        ? activeItem
                        : null
                    }
                    variant="ghost"
                    className={cn(
                      'group h-fit justify-start gap-2 px-2 py-1.5',
                      isActive && 'bg-background-secondary'
                    )}
                    size="xs"
                    onClick={createCommandClickHandler(groupIndex, commandIndex)}
                  >
                    {command.icon && <command.icon className="min-w-3.5" size={14} />}
                    <div className="relative flex w-full items-center">
                      <div className="flex w-full max-w-full items-center justify-between gap-2">
                        {command.content}
                        {group.name === 'snippets' && command.description && (
                          <Button
                            disableAutomaticSvgSizing
                            size="xs"
                            variant="ghost"
                            className={cn(
                              'h-fit min-w-fit p-1 opacity-0 transition-opacity hover:bg-background-secondary-hover group-hover:opacity-100',
                              isActive && 'opacity-100'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              props.editor.chain().focus().deleteRange(props.range).run();
                              setIsOpen({
                                open: true,
                                from: 'editor',
                                isEditing: true,
                                initialSnippet: {
                                  shorthand: command.name,
                                  content: command.description ?? '',
                                  is_verbal_trigger:
                                    command.snippetIsVerbalTrigger ?? false,
                                  trigger_phrase: command.snippetTriggerPhrase ?? '',
                                  id: command.snippetId,
                                },
                              });
                            }}
                          >
                            <Pencil className="min-w-3" size={12} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});

CommandList.displayName = 'CommandList';
